import React from "react";
import {
  Typography as MuiTypography,
  Divider as MuiDivider,
} from "@material-ui/core";
import styled from "styled-components/macro";
import { spacing } from "@material-ui/system";
import { Helmet } from "react-helmet-async";
import { z } from "zod";

import useQueryParams from "../../hooks/useQueryParams";
import RawTable from "../../components/RawTable";
import ClimateMonthlyChartContainer from "../../components/charts/climateMonthlyChart/ClimateMonthlyChartContainer";
import ClimateMonthlyControls from "./ClimateMonthlyControls";
import { useClimateWaterYears } from "../../hooks/charts/useClimateMonthlySeries";
import {
  CLIMATE_PARAM_SHAPE,
  PAGE_TITLE,
  STATIONS,
  VARIABLES,
} from "./climateCopy";

const Section = styled.section`
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
`;
const Typography = styled(MuiTypography)(spacing);
const Divider = styled(MuiDivider)(spacing);

const schema = z.object(CLIMATE_PARAM_SHAPE);

/**
 * Decides whether the Cherry Creek MET station can be drawn at all: it only
 * can when at least one selected water year falls inside its record.
 */
export const mhfdUnavailable = (params) =>
  ![params.water_year, ...params.compare].some(
    (year) => year >= STATIONS.MHFD.firstCompleteWaterYear
  );
export default function ClimateMonthly() {
  const { params, setParams } = useQueryParams(schema);
  const { waterYears } = useClimateWaterYears();

  const config = VARIABLES[params.variable] ?? VARIABLES.precip;
  const mhfdDisabled = mhfdUnavailable(params);

  /* Declared inside the component on purpose. material-table mutates the
     columns array it is handed, writing its own tableData onto each entry. A
     module-level array keeps those mutations between renders and they
     compound; every other page in this repo builds its columns inline for the
     same reason. */
  const RAW_TABLE_COLUMNS = [
    { title: "Location", field: "location_desc" },
    { title: "Water Year", field: "water_year" },
    { title: "Month", field: "month_abbrev" },
    { title: "Precipitation (in)", field: "precip_total_in", type: "numeric" },
    { title: "Avg Daily Max (°F)", field: "tmax_avg_f", type: "numeric" },
    { title: "Avg Daily Min (°F)", field: "tmin_avg_f", type: "numeric" },
    {
      title: "Highest Daily Max (°F)",
      field: "tmax_extreme_f",
      type: "numeric",
    },
    {
      title: "Lowest Daily Min (°F)",
      field: "tmin_extreme_f",
      type: "numeric",
    },
    { title: "Days of Data", field: "days_with_data", type: "numeric" },
  ];

  /* The picker needs the current water year in the list even before the first
     October reading of it lands. */
  const yearOptions = waterYears.length
    ? waterYears
    : [params.water_year].filter(Boolean);

  /* Changing the water year drops it from the comparison chips, so the same
     year can never be drawn twice. */
  const handleFilters = (updater) =>
    setParams((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      const waterYear = Number(next.water_year);
      return {
        ...next,
        compare: (next.compare ?? []).filter((year) => year !== waterYear),
      };
    });

  return (
    <>
      <Helmet title={PAGE_TITLE} />
      <header>
        <Typography variant="h3" component="h1" gutterBottom>
          {PAGE_TITLE}
        </Typography>
        <Divider my={4} />
      </header>

      <main>
        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            About this Page
          </Typography>
          <Typography variant="body1" gutterBottom>
            This page compares a water year's{" "}
            <strong>monthly precipitation</strong> and{" "}
            <strong>air temperature</strong> against the long-term average for
            the same month. Months run <strong>October to September</strong> to
            follow the water year.
          </Typography>
          <Typography variant="body1" gutterBottom>
            Two stations report in the basin:{" "}
            <strong>Denver Centennial Airport (KAPA)</strong>, a National
            Weather Service station with a record back to{" "}
            <strong>July 1998</strong>, and the{" "}
            <strong>Cherry Creek MET station</strong> operated by Mile High
            Flood District, which has reported since <strong>June 2021</strong>.
          </Typography>
          <Typography variant="body1" gutterBottom>
            The long-term average is taken from KAPA over{" "}
            <strong>complete water years only</strong>. The annual report uses{" "}
            <strong>2006</strong> through the most recent complete water year,
            which is the default here; the <strong>Normal Start Year</strong>{" "}
            filter moves it back as far as 1999.
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Tip:</strong> As you adjust the filters, the chart and table
            update automatically. The page URL updates too, so you can{" "}
            <strong>bookmark</strong> or
            <strong> share a direct link</strong> to your current view.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Filters
          </Typography>
          <ClimateMonthlyControls
            params={params}
            onChange={handleFilters}
            waterYears={yearOptions}
          />
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            {config.label} by Water-Year Month, WY{params.water_year}
          </Typography>
          <ClimateMonthlyChartContainer
            variable={params.variable}
            waterYear={params.water_year}
            compareYears={params.compare}
            normalStart={params.normal_start}
            showKapa={params.kapa}
            showMhfd={params.mhfd && !mhfdDisabled}
            units={params.units}
            allWaterYears={yearOptions}
            mhfdDisabled={mhfdDisabled}
            onCompareYearsChange={(compare) =>
              setParams({ ...params, compare })
            }
            onUnitsChange={(units) => setParams({ ...params, units })}
            onShowKapaChange={(show) =>
              setParams({ ...params, kapa: show ? 1 : 0 })
            }
            onShowMhfdChange={(show) =>
              setParams({ ...params, mhfd: show ? 1 : 0 })
            }
          />
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            About this Graph
          </Typography>
          <Typography variant="body1" gutterBottom>
            The <strong>line</strong> is the long-term average, drawn across the
            months as a reference level; the <strong>bars</strong> are the
            selected water year at each station. A month with{" "}
            <strong>no data</strong> at a station is left blank rather than
            drawn as zero, so a gap always means a gap.
          </Typography>
          <Typography variant="body1" gutterBottom>
            A month that is <strong>still in progress</strong>, or that is
            missing days, is drawn <strong>faded</strong> and listed beneath the
            chart. Hover any month to read the value, how far it sits{" "}
            <strong>above or below the average</strong>, and how many days of
            data went into it.
          </Typography>
          <Typography variant="body1" gutterBottom>
            Air temperature defaults to <strong>°F</strong> to match the annual
            report, with a <strong>°C</strong> option in the graph filters. Note
            that reservoir water temperatures elsewhere on this site are
            reported in °C.
          </Typography>
          <Typography variant="body1" gutterBottom>
            The chart saves as a <strong>PNG</strong> and its figures download
            as <strong>CSV</strong> using the buttons above the graph.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Raw Data Table
          </Typography>
          <RawTable
            endPoint="climate-monthly"
            params={{
              water_years: [params.water_year, ...params.compare].join(","),
              /* Follows the station chips, so the table shows what the
                 chart shows. With both switched off the route falls back to
                 all stations rather than returning nothing. */
              locationids: [
                ...(params.kapa ? [STATIONS.KAPA.locationid] : []),
                ...(params.mhfd && !mhfdDisabled
                  ? [STATIONS.MHFD.locationid]
                  : []),
              ].join(","),
            }}
            columns={RAW_TABLE_COLUMNS}
            decimals={2}
          />
        </Section>
      </main>
    </>
  );
}
